import { useEffect, useRef, useState } from "react"

import { RaceShield } from "@/components/race-shield"
import { TeamName } from "@/components/team-name"
import type { MatchPageProps } from "@/types"

// ── Score count-up animation ──────────────────────────────────────────

function AnimatedScore({
  target,
  className,
  style,
}: {
  target: number
  className?: string
  style?: React.CSSProperties
}) {
  const [display, setDisplay] = useState(0)
  const startedRef = useRef(false)

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true

    if (target === 0) return

    const duration = 800
    const start = performance.now()

    function tick(now: number) {
      const elapsed = now - start
      const progress = Math.min(elapsed / duration, 1)
      // easeOutExpo
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress)
      setDisplay(Math.round(eased * target))
      if (progress < 1) requestAnimationFrame(tick)
    }

    requestAnimationFrame(tick)
  }, [target])

  return (
    <span className={className} style={style}>
      {display}
    </span>
  )
}

// ── Broadcast Scoreboard (completed matches) ──────────────────────────

export function BroadcastScoreboard({
  match,
  userTeamId,
}: {
  match: MatchPageProps["match"]
  userTeamId: string | null
}) {
  const homeIsUser = userTeamId === match.home_team.id
  const awayIsUser = userTeamId === match.away_team.id

  return (
    <div className="py-8 text-center">
      <div className="flex items-center justify-center gap-6 md:gap-12">
        {/* Home team */}
        <div className="min-w-0 flex-1 text-center">
          <RaceShield
            race={match.home_team.race}
            hue={match.home_team.color_hue ?? undefined}
            size="lg"
            className="mx-auto mb-2"
          />
          <h2 className="font-display text-xl leading-none font-bold">
            <TeamName
              name={match.home_team.name}
              hue={match.home_team.color_hue ?? 55}
              isUser={homeIsUser}
            />
          </h2>
          <div
            className="mt-1 text-[10px] font-bold tracking-widest uppercase"
            style={{ color: "var(--muted-foreground)" }}
          >
            {match.home_team.race}
          </div>
          <div
            className="mt-0.5 text-[10px]"
            style={{ color: "var(--muted-foreground)" }}
          >
            Coach {match.home_team.coach_name}
          </div>
        </div>

        {/* Score */}
        <div className="flex-shrink-0 text-center">
          {match.home_score !== null && match.away_score !== null && (
            <>
              <div className="flex items-center gap-3">
                <AnimatedScore
                  target={match.home_score}
                  className="font-display text-[5rem] leading-none font-bold tracking-[0.02em]"
                  style={{
                    color: "var(--neon-gold)",
                    textShadow:
                      "0 0 8px oklch(0.85 0.28 85 / 0.6), 0 0 20px oklch(0.75 0.22 85 / 0.25), 0 1px 0 oklch(0.55 0.14 85), 0 2px 4px oklch(0 0 0 / 0.5)",
                  }}
                />
                <span
                  className="font-display text-2xl font-bold"
                  style={{ color: "oklch(0.30 0.04 55)" }}
                >
                  —
                </span>
                <AnimatedScore
                  target={match.away_score}
                  className="font-display text-[5rem] leading-none font-bold tracking-[0.02em]"
                  style={{
                    color: "var(--neon-gold)",
                    textShadow:
                      "0 0 8px oklch(0.85 0.28 85 / 0.6), 0 0 20px oklch(0.75 0.22 85 / 0.25), 0 1px 0 oklch(0.55 0.14 85), 0 2px 4px oklch(0 0 0 / 0.5)",
                  }}
                />
              </div>
              {match.extra_time_played && (
                <div
                  className="mt-2 text-[10px] font-bold tracking-[0.3em]"
                  style={{ color: "var(--muted-foreground)" }}
                  data-testid="aet-badge"
                >
                  AFTER EXTRA TIME
                </div>
              )}
            </>
          )}
        </div>

        {/* Away team */}
        <div className="min-w-0 flex-1 text-center">
          <RaceShield
            race={match.away_team.race}
            hue={match.away_team.color_hue ?? undefined}
            size="lg"
            className="mx-auto mb-2"
          />
          <h2 className="font-display text-xl leading-none font-bold">
            <TeamName
              name={match.away_team.name}
              hue={match.away_team.color_hue ?? 280}
              isUser={awayIsUser}
            />
          </h2>
          <div
            className="mt-1 text-[10px] font-bold tracking-widest uppercase"
            style={{ color: "var(--muted-foreground)" }}
          >
            {match.away_team.race}
          </div>
          <div
            className="mt-0.5 text-[10px]"
            style={{ color: "var(--muted-foreground)" }}
          >
            Coach {match.away_team.coach_name}
          </div>
        </div>
      </div>
    </div>
  )
}
